import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAppContext } from '../context/AppContext';

const TaskLog = ({ isAgency = false }) => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { tasks, clients, agencies, isLoading } = useAppContext();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [clientFilter, setClientFilter] = useState('');

  const agency = agencies.find(item => item.id === agencyId);
  const hasAgencyAccess = isAgency && Cookies.get(`shift_auth_${agencyId}`) === 'true';
  const agencyClients = isAgency ? clients.filter(client => client.agencyId === agencyId) : clients;
  const clientIds = new Set(agencyClients.map(client => client.id));

  const visibleTasks = useMemo(() => tasks
    .filter(task => !isAgency || clientIds.has(task.clientId))
    .filter(task => statusFilter === 'All' || (task.status || 'Open') === statusFilter)
    .filter(task => !clientFilter || task.clientId === clientFilter)
    .filter(task => {
      const client = agencyClients.find(item => item.id === task.clientId);
      const query = search.trim().toLowerCase();
      return !query ||
        (task.title ?? '').toLowerCase().includes(query) ||
        (client?.name ?? '').toLowerCase().includes(query);
    })
    .sort((first, second) => {
      const firstDate = new Date(first.last_updated || first.date || 0).getTime();
      const secondDate = new Date(second.last_updated || second.date || 0).getTime();
      return (Number.isFinite(secondDate) ? secondDate : 0) - (Number.isFinite(firstDate) ? firstDate : 0);
    }), [tasks, agencyClients, clientIds, isAgency, statusFilter, clientFilter, search]);

  if (isAgency && !isLoading && (!agency || !hasAgencyAccess)) {
    return <Navigate to={`/portal/${agencyId}`} replace />;
  }

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? '—'
      : date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const content = (
    <div className="space-y-6 sm:space-y-8 pb-10">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-teal-500 font-bold text-sm uppercase tracking-widest mb-1">
            {isAgency ? `${agency?.name ?? 'Agency'} Activity` : 'Support Activity'}
          </p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Task Log</h1>
          <p className="text-slate-500 mt-2">Chronological view of support tasks, updates, and logged time.</p>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-2xl px-5 py-3">
          <p className="text-xs text-slate-500 uppercase font-black tracking-widest">Tasks shown</p>
          <p className="text-2xl font-black text-teal-400">{visibleTasks.length}</p>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="relative">
          <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search tasks or clients..."
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-11 pr-4 py-3.5 text-sm text-white outline-none focus:ring-2 focus:ring-teal-500/50"
          />
        </div>
        <select
          value={clientFilter}
          onChange={event => setClientFilter(event.target.value)}
          className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3.5 text-sm text-white outline-none focus:ring-2 focus:ring-teal-500/50"
          aria-label="Filter task log by client"
        >
          <option value="">All Clients</option>
          {agencyClients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={event => setStatusFilter(event.target.value)}
          className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3.5 text-sm text-white outline-none focus:ring-2 focus:ring-teal-500/50"
          aria-label="Filter task log by status"
        >
          {['All', 'Open', 'Closed', 'Archived'].map(status => (
            <option key={status} value={status}>{status === 'All' ? 'All Statuses' : status}</option>
          ))}
        </select>
      </section>

      <section className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead>
              <tr className="bg-slate-800/30 border-b border-slate-800">
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Updated</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Task</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Client</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Logged</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {visibleTasks.map(task => {
                const client = agencyClients.find(item => item.id === task.clientId);
                const taskPath = isAgency
                  ? `/portal/${agencyId}/task/${task.id}`
                  : `/tasks/${task.id}`;

                return (
                  <tr
                    key={task.id}
                    onClick={() => navigate(taskPath)}
                    className="cursor-pointer hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="px-6 py-5 text-sm text-slate-400 whitespace-nowrap">{formatDate(task.last_updated || task.date)}</td>
                    <td className="px-6 py-5">
                      <p className="font-bold text-white hover:text-teal-400">{task.title || 'Untitled Task'}</p>
                      {(task.messages ?? []).length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {(task.messages ?? []).length} update{(task.messages ?? []).length === 1 ? '' : 's'}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-5 text-sm font-bold text-slate-300">{client?.name ?? 'Unknown'}</td>
                    <td className="px-6 py-5">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${
                        task.status === 'Open'
                          ? 'bg-teal-500/10 text-teal-400'
                          : task.status === 'Archived'
                            ? 'bg-amber-500/10 text-amber-400'
                            : 'bg-slate-800 text-slate-400'
                      }`}>
                        {task.status || 'Open'}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right font-mono font-black text-teal-400">
                      {(Number(task.loggedHours) || 0).toFixed(1)}h
                    </td>
                  </tr>
                );
              })}
              {visibleTasks.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500 font-bold">
                    {isLoading ? 'Loading task log...' : 'No task activity matches these filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );

  if (!isAgency) return content;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">{content}</div>
    </div>
  );
};

export default TaskLog;